import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { tokenStore } from "@/lib/tokenStore";

const AuthContext = createContext(null);

// NOTE on storage: We use localStorage for the JWT (bearer token) because the
// frontend is a standard SPA hosted on a separate origin from the backend, which
// makes httpOnly cookie auth non-trivial without a unified domain + CSRF strategy.
// The token is short-lived (7 days) and never contains PII; XSS exposure is the
// main risk and is mitigated by CSP headers (configured at hosting level), strict
// dependency hygiene, and React's default JSX escaping.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check; AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokenStore.set(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    tokenStore.set(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const setSessionToken = useCallback(async (token) => {
    tokenStore.set(token);
    await fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed, clearing local session anyway", err);
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, setSessionToken, refresh: fetchMe }),
    [user, loading, login, register, logout, setSessionToken, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
